// Port of sections/home/WhatWeDoMap/geometry.ts without the `@/constants/what-we-do` import (which
// pulls the `@/utils/models` barrel into the client chunk): the relations come in as data from the
// server component instead.

export type NodeKind = 'industry' | 'service' | 'product';

export type WhatWeDoSelection = { kind: NodeKind; id: string } | { kind: 'core' };

export type WhatWeDoRelations = {
  services: { id: string; industries: string[] }[];
  products: { id: string; services: string[]; industries: string[] }[];
};

export type ActiveSets = { services: Set<string>; industries: Set<string>; products: Set<string> };

/** Everything connected to the selection. `null` for no selection or the core (= everything). */
export const computeActiveSets = (
  selection: WhatWeDoSelection | null,
  relations: WhatWeDoRelations,
): ActiveSets | null => {
  if (!selection || selection.kind === 'core') return null;
  const { services, products } = relations;

  if (selection.kind === 'service') {
    const service = services.find((s) => s.id === selection.id);
    return {
      services: new Set([selection.id]),
      industries: new Set(service?.industries ?? []),
      products: new Set(products.filter((p) => p.services.includes(selection.id)).map((p) => p.id)),
    };
  }

  if (selection.kind === 'industry') {
    return {
      services: new Set(services.filter((s) => s.industries.includes(selection.id)).map((s) => s.id)),
      industries: new Set([selection.id]),
      products: new Set(products.filter((p) => p.industries.includes(selection.id)).map((p) => p.id)),
    };
  }

  const product = products.find((p) => p.id === selection.id);
  return {
    services: new Set(product?.services ?? []),
    industries: new Set(product?.industries ?? []),
    products: new Set([selection.id]),
  };
};

export type SegmentState = 'idle' | 'on' | 'dim' | 'self';

export const segmentState = (
  kind: NodeKind,
  id: string,
  selection: WhatWeDoSelection | null,
  active: ActiveSets | null,
): SegmentState => {
  if (selection?.kind === 'core') return 'on';
  if (!selection || !active) return 'idle';
  if (selection.kind === kind && selection.id === id) return 'self';
  const set = kind === 'service' ? active.services : kind === 'industry' ? active.industries : active.products;
  return set.has(id) ? 'on' : 'dim';
};

/** Vertical S-curve between two anchor points (bottom of one node, top of the next). */
export const connectorPath = (x1: number, y1: number, x2: number, y2: number) => {
  const midY = (y1 + y2) / 2;
  return `M ${x1.toFixed(1)} ${y1.toFixed(1)} C ${x1.toFixed(1)} ${midY.toFixed(1)}, ${x2.toFixed(1)} ${midY.toFixed(
    1,
  )}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
};

export type LinkState = 'idle' | 'on' | 'self' | 'dim';

/** A connector lights up when both ends are lit; it goes lime when one end is the selected node. */
export const linkState = (a: SegmentState, b: SegmentState, hasSelection: boolean): LinkState => {
  if (!hasSelection) return 'idle';
  const lit = (st: SegmentState) => st === 'on' || st === 'self';
  if (!lit(a) || !lit(b)) return 'dim';
  return a === 'self' || b === 'self' ? 'self' : 'on';
};
